import {
  Crossmark,
  Spinner,
  SpinnerContainer,
  useCurrentWindow,
  useDynamicCallback,
  useIntlContext,
  VStack,
  type ResolutionEnumNoPrefix,
} from '@talos/kyoko';
import type { ResolutionString } from '@trading-view/charting_library';
import {
  widget,
  type AccessList,
  type ChartingLibraryWidgetOptions,
  type IBasicDataFeed,
  type IChartingLibraryWidget,
  type ISubscription,
  type Timezone,
} from '@trading-view/charting_library';
import { useCallback, useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';
import { Chart } from './styles';
import { useTradingViewDatafeed } from './useTradingViewDatafeed';
import { useTradingViewWidgetOptions } from './useTradingViewWidgetOptions';
import { fromTradingViewResolution, toTradingViewResolution } from './utils';

const STATE = 'tradingView.autosave';

export interface TradingViewChartProps {
  symbol: string;
  markets?: string[];
  apiEndpoint: string;
  resolution?: ResolutionEnumNoPrefix | undefined;
  onResolutionChange?: (newInterval: ResolutionEnumNoPrefix) => void;
  chartId: string;
  /** The exchange name to appear in the Object Tree for this chart.
   *  For Whitelabel, this is the client name.
   *  For Ava, this is currently 'talos' */
  exchangeName: string;
  studiesAccess?: AccessList['tools'][number]['name'][];
  /** If true, hides the volume indicator in the chart */
  hideTradingViewVolume?: boolean;
  /** If true, enables additional non-native OHLC resolutions (10M, 15M, 30M, 1W, 1MO) */
  enableNonNativeOHLCResolutions?: boolean;
}

export const TradingViewChart = (params: PropsWithChildren<TradingViewChartProps>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const interval = useRef<ResolutionString>(
    params.resolution ? (toTradingViewResolution(params.resolution) as ResolutionString) : ('60' as ResolutionString)
  );
  const tradingviewWidget = useRef<IChartingLibraryWidget | null>(null);
  const isLoadingRef = useRef<boolean>(true);
  const datafeed: IBasicDataFeed = useTradingViewDatafeed(params);
  const theme = useTheme();
  const { children, onResolutionChange } = params;

  const { locale } = useIntlContext();

  const { widgetOptions: baseWidgetOptions, overrides } = useTradingViewWidgetOptions({
    symbol: params.symbol,
    studiesAccess: params.studiesAccess,
    hideTradingViewVolume: params.hideTradingViewVolume,
    interval: interval.current as ResolutionString,
    locale,
  });

  const getChartId = useDynamicCallback(() => {
    return `${STATE}/${params.chartId}`;
  });

  const onAutoSaveNeeded = useCallback(() => {
    tradingviewWidget.current?.save(state => localStorage.setItem(getChartId(), JSON.stringify(state)));
  }, [tradingviewWidget, getChartId]);

  const handleIntervalChange = useDynamicCallback((newInterval: ResolutionString) => {
    datafeed.unsubscribeBars('');
    interval.current = newInterval;
    onResolutionChange?.(fromTradingViewResolution(newInterval));
  });

  const currentWindow = useCurrentWindow();
  useEffect(() => {
    let intervalChangedSub: ISubscription<(newInterval: ResolutionString) => void>;
    let timeoutId: ReturnType<typeof setTimeout>;

    if (params.symbol != null) {
      setIsLoading(true);
      isLoadingRef.current = true;
      setHasTimedOut(false);

      // Set up 30 second timeout
      timeoutId = setTimeout(() => {
        if (isLoadingRef.current) {
          setHasTimedOut(true);
        }
      }, 30_000);

      const { backgroundChartUpColor, backgroundChartDownColor, borderColorChartUpColor, borderColorChartDownColor } =
        theme;

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone;

      const widgetOptions: ChartingLibraryWidgetOptions = {
        ...baseWidgetOptions,
        container: containerRef.current as HTMLElement,
        datafeed: datafeed,
      };

      tradingviewWidget.current = new widget(widgetOptions);

      // If currentWindow is not the same as window, we need to copy the tradingview instance to the current window
      // This means we're in a popout but the tradingview instance is loaded in the parent window context
      if (currentWindow !== window) {
        // The tradingview instance has a unique id that we can use to copy the instance to the current window
        const tradingViewKey = (tradingviewWidget.current as any)._id;
        (currentWindow as any).TradingView = window.TradingView;
        (currentWindow as any)[tradingViewKey] = (window as any)[tradingViewKey];
      }

      tradingviewWidget.current.onChartReady(() => {
        if (!tradingviewWidget.current) {
          return;
        }
        const savedState = localStorage.getItem(getChartId());
        if (savedState) {
          // What we save to local storage includes the user's system timezone info as it was at the time of saving.
          // This causes problems if the user then changes their system timezone as their chart continue to display in the previous timezone.
          // The below searches for the timezone key in the saved state and replaces it with the current timezone.
          // This is flakey in that we do not control the format of this state, however the string replacement is less flakey than finding the exact location in the object and replacing it there (allowing tradingview to move the location at least and this will still work).
          const processedState = savedState.replace(/"timezone":"[^"]+"/, `"timezone":"${timezone}"`);
          tradingviewWidget.current.load(JSON.parse(processedState));
        }
        setIsLoading(false);
        isLoadingRef.current = false;
        setHasTimedOut(false);
        tradingviewWidget.current.subscribe('onAutoSaveNeeded', onAutoSaveNeeded);
        tradingviewWidget.current.applyOverrides(overrides);
        const chart = tradingviewWidget.current.activeChart();
        intervalChangedSub = chart.onIntervalChanged();
        intervalChangedSub.subscribe(null, handleIntervalChange);
        const series = chart.getSeries();
        chart.executeActionById('chartReset');
        series.setChartStyleProperties(1, {
          upColor: backgroundChartUpColor,
          downColor: backgroundChartDownColor,
          drawWick: true,
          drawBorder: true,
          borderColor: '#aaa',
          borderUpColor: borderColorChartUpColor,
          borderDownColor: borderColorChartDownColor,
          wickColor: '#aaa',
          wickUpColor: borderColorChartUpColor,
          wickDownColor: borderColorChartDownColor,
          barColorsOnPrevClose: true,
        });
      });
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (tradingviewWidget.current) {
        datafeed.unsubscribeBars('');
        intervalChangedSub?.unsubscribe(null, handleIntervalChange);
        tradingviewWidget.current.remove();
        tradingviewWidget.current = null;
      }
    };
  }, [
    baseWidgetOptions,
    currentWindow,
    datafeed,
    getChartId,
    handleIntervalChange,
    onAutoSaveNeeded,
    overrides,
    params.symbol,
    theme,
  ]);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {(params.symbol == null || isLoading) && (
        <SpinnerContainer>
          {hasTimedOut ? (
            <VStack gap="spacing16" mb="-spacing32">
              <Crossmark />
              <div>The chart could not be loaded</div>
            </VStack>
          ) : (
            <Spinner />
          )}
        </SpinnerContainer>
      )}
      <Chart isHidden={isLoading} ref={containerRef} />
      {children}
    </>
  );
};
